import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { Spinner } from '@acorns/web-components'
import { useFeature } from '@acorns/web-toggles-react'
import { useAnalyticsClient } from '@acorns/web-utils'
import { useQuery } from '@apollo/react-hooks'

import SidebarFrame from 'components/SidebarFrame'
import { ssnCollisionKyc } from 'constants/feature-keys'
import { User } from 'generated/types'
import { Survey } from 'pages/closed-account/Survey'

import { Confirmation } from './Confirmation'
import userQuery from './gql/user.query.gql'
import { NextSteps } from './NextSteps'
import { PortfolioPreview } from './PortfolioPreview'
import { Pricing } from './PricingInterstitial'
import { ReopenSuccess } from './ReopenSuccess'
import { Loader } from './styled-components'
import { WelcomeBack } from './WelcomeBack'

export type UserResponse = {
  user: User
}

export type StepName =
  | 'Welcome'
  | 'Confirmation'
  | 'Pricing'
  | 'Portfolio'
  | 'Reopen'
  | 'Survey'
  | 'NextSteps'

export type GetStep = (stepName: StepName) => number

export enum steps {
  WelcomeBack = 0,
  NextSteps = 1,
  Confirmation = 2,
  Pricing = 3,
  Portfolio = 4,
  Reopen = 5,
  Survey = 6,
}

export const stepsCollection: Array<(props?: any) => JSX.Element> = [
  WelcomeBack,
  NextSteps,
  Confirmation,
  Pricing,
  PortfolioPreview,
  ReopenSuccess,
  Survey,
]

export enum stepsLongReopen {
  WelcomeBack = 0,
  NextSteps = 1,
  Confirmation = 2,
  Survey = 3,
}

export const stepsCollectionLongReopen: Array<(props?: any) => JSX.Element> = [
  WelcomeBack,
  NextSteps,
  Confirmation,
  Survey,
]

export const SURVEY_COMPLETED_KEY = 'closedAccount:surveyCompleted'
export const ACCOUNT_FOUND_KEY = 'closedAccount:accountFound'

export const ClosedAccount = ({ isLongReopen }: { isLongReopen?: boolean }) => {
  const history = useHistory()
  const analytics = useAnalyticsClient()

  const getStep: GetStep = (stepName: StepName) => {
    if (isLongReopen) {
      if (stepName === 'Welcome') {
        return stepsLongReopen.WelcomeBack
      }
      if (stepName in stepsLongReopen) {
        return stepsLongReopen[stepName as keyof typeof stepsLongReopen]
      }
    }
    if (stepName === 'Welcome') {
      return steps.WelcomeBack
    }
    return steps[stepName]
  }

  const [isSsnCollisionKycEnabled] = useFeature(ssnCollisionKyc)
  const location = useLocation()
  const isFromAccountFound =
    isSsnCollisionKycEnabled &&
    (new URLSearchParams(location.search).get('from') === 'accountFound' ||
      sessionStorage.getItem(ACCOUNT_FOUND_KEY) === 'true')

  useEffect(() => {
    if (isFromAccountFound) {
      sessionStorage.setItem(ACCOUNT_FOUND_KEY, 'true')
    }
  }, [isFromAccountFound])

  const getInitialStep = () => {
    if (isFromAccountFound) {
      return getStep('NextSteps')
    }
    if (sessionStorage.getItem(SURVEY_COMPLETED_KEY)) {
      return getStep('Welcome')
    }
    return getStep('Survey')
  }

  const [reopenFlow, setReopenFlow] = useState(getInitialStep)

  const { data, loading: isLoadingUser } = useQuery<UserResponse, any>(
    userQuery,
  )
  const [lastLocation, setLastLocation] = useState('/closed-account')

  useEffect(() => {
    if (data?.user && !data?.user?.closedAt) {
      history.replace('/present')
    }
  }, [data])

  // Without this code there is not pathname on the url for closed-account.
  // This code ensures that the pathname is closed-account, even when trying to navigate to a different route.
  useEffect(() => {
    if (lastLocation === '/closed-account') {
      history.push('/closed-account')
    }
    setLastLocation(location)
  }, [location])

  if (isLoadingUser) {
    return (
      <Loader>
        <Spinner />
      </Loader>
    )
  }

  const getCurrentStep = () => {
    if (isLongReopen) {
      const step = stepsCollectionLongReopen[reopenFlow]
      return step ?? stepsCollectionLongReopen[stepsLongReopen.WelcomeBack]
    }
    return stepsCollection[reopenFlow]
  }

  const CurrentStep = getCurrentStep()

  return (
    <SidebarFrame hideFooter>
      <CurrentStep
        isLongReopen={isLongReopen ?? false}
        setStep={setReopenFlow}
        user={data?.user}
        analytics={analytics}
        getStep={getStep}
      />
    </SidebarFrame>
  )
}
